import { errorToast } from "@/v2-ui/toast"
import { useLayerQuery } from "@/v2-backoffice-client/layer/layer.query"
import { useLayerCategoryQuery } from "@/v2-backoffice-client/layer/category/layer.category.query"
import type { LayerCategoryFragment } from "@/v2-backoffice-client/layer/category/__types__/layer.category.fragment"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"

type UseLayerOrganizeCatalogInput = {
  skip: boolean
}

/**
 * Composed catalog query for the organizer: the dialog needs BOTH the layer
 * list and the category list before either tab can render (the loading gate
 * shows until both arrive, and the categories tab joins layers into their
 * categories), so the two queries live behind this one hook to keep the
 * dialog at a single query hook.
 */
export function useLayerOrganizeCatalog(input: UseLayerOrganizeCatalogInput) {
  const { skip } = input

  const layersQuery = useLayerQuery({ skip })
  errorToast(layersQuery.error, { title: "Failed to load the layer list" })
  const categoriesQuery = useLayerCategoryQuery({ skip })
  errorToast(categoriesQuery.error, {
    title: "Failed to load the category list",
  })

  const layers = layersQuery.data?.getLayers ?? []
  const categories = categoriesQuery.data?.getLayerCategories ?? []

  const layersByCode = new Map<string, LayerFragment>()
  for (const layer of layers) {
    layersByCode.set(layer.FeatureCode, layer)
  }

  const categoriesByCode = new Map<string, LayerCategoryFragment>()
  for (const category of categories) {
    categoriesByCode.set(category.FeatureCode, category)
  }

  return {
    layers,
    categories,
    layersByCode,
    categoriesByCode,
    isLoading: layersQuery.loading || categoriesQuery.loading,
  }
}
